import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, Tooltip, Menu, MenuItem, Avatar, Chip } from '@mui/material';
import ManIcon from '@mui/icons-material/Man';
import WomanIcon from '@mui/icons-material/Woman';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import CachedImage from '../../offline/components/CachedImage';

// Safe date parsing supporting "YYYY-MM-DD HH:MM:SS"
const parseDateTime = (date, time) => {
  if (!date) return null;
  const dt = time ? `${date} ${time}` : date;
  const iso = dt.includes(' ') ? dt.replace(' ', 'T') : dt;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

// Determine gender string from various possible fields
const getGender = (emp) => {
  const g = (emp?.gender || emp?.sex || emp?.genderType || '').toString().toLowerCase();
  if (g.startsWith('f')) return 'female';
  if (g.startsWith('m')) return 'male';
  return 'unknown';
};

// Render colored Avatar fallback by gender
const ColoredAvatar = ({ emp, name, size = 28 }) => {
  const gender = getGender(emp);
  const bg = gender === 'female' ? '#f48fb1' : gender === 'male' ? '#90caf9' : '#bdbdbd';
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: bg, color: '#fff' }}>
      {gender === 'female' ? (
        <WomanIcon sx={{ fontSize: size * 0.7 }} />
      ) : gender === 'male' ? (
        <ManIcon sx={{ fontSize: size * 0.7 }} />
      ) : (
        <PersonIcon sx={{ fontSize: size * 0.7 }} />
      )}
    </Avatar>
  );
};

const formatElapsed = (start) => {
  if (!start) return '00:00:00';
  const diff = Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000));
  const h = String(Math.floor(diff / 3600)).padStart(2, '0');
  const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
  const s = String(diff % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

// Try to resolve an image URL from employee object in different possible fields
const resolveEmpImage = (emp) => {
  if (!emp) return null;
  return (
    emp.profileImage ||
    emp.photo ||
    emp.imageUrl ||
    emp.image ||
    emp.staff_photo ||
    emp.profilePic ||
    emp.photoUrl ||
    (emp.image && emp.image.url) ||
    (emp.profile && (emp.profile.imageUrl || emp.profile.photo || emp.profile.profileImage)) ||
    null
  );
};

// Main small indicator used in Dashboard header
const CheckInStatusIndicator = ({ onCheckoutClick }) => {
  const userInfo = useSelector((s) => s.loginResponseSlice.value);
  const userLoginMeta = typeof window !== 'undefined' ? localStorage.getItem('login_meta') : null;
  const employeeId = userLoginMeta ? JSON.parse(userLoginMeta)?.data?._id : userInfo?.data?._id;

  // Check-in summary from Report slice (loaded in Dashboard useEffect)
  const checkinSummary = useSelector((s) => s.posReportshiftSlice.shiftReporCheckinSummary);
  const activeEmployees = useSelector((s) => s.allActiveEmployeeSlice.value) || [];

  const allCheckins = checkinSummary?.empCheckIn || [];

  const activeCheckins = useMemo(() => {
    return allCheckins.filter((c) => {
      const hasEnded = c?.checkOut === true || c?.checkOut === "true" || !!c?.checkOutDate || !!c?.checkOutTime;
      return !hasEnded;
    });
  }, [allCheckins]);

  // Current user's check-in record
  const myCheckin = useMemo(() => {
    return activeCheckins.find((c) => c?.empData?._id === employeeId || c?.empId === employeeId);
  }, [activeCheckins, employeeId]);

  // Derive employee object for image/name fallback
  // Prioritize activeEmployees (fresh data) over empData (cached in check-in)
  const myEmp = useMemo(() => {
    const freshEmp = activeEmployees.find((e) => e?._id === employeeId);
    if (freshEmp) return freshEmp;
    return myCheckin?.empData;
  }, [myCheckin, activeEmployees, employeeId]);

  const start = useMemo(() => parseDateTime(myCheckin?.checkInDate, myCheckin?.checkInTime), [myCheckin]);
  const [elapsed, setElapsed] = useState(formatElapsed(start));

  useEffect(() => {
    setElapsed(formatElapsed(start));
    if (!start) return;
    const id = setInterval(() => setElapsed(formatElapsed(start)), 1000);
    return () => clearInterval(id);
  }, [start]);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const open = Boolean(menuAnchor);
  const handleOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleClose = () => setMenuAnchor(null);

  // Build other checked-in employees list (excluding current)
  const others = useMemo(() => activeCheckins.filter((c) => (c?.empData?._id || c?.empId) !== employeeId), [activeCheckins, employeeId]);

  if (!myCheckin) return null; // Only visible when user is checked-in

  const imgUrl = resolveEmpImage(myEmp);
  const name = myEmp?.staff_name || userInfo?.userName || 'Employee';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
      {imgUrl ? (
        <CachedImage
          src={imgUrl}
          alt={name}
          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
          fallbackStyle={{ width: 28, height: 28, borderRadius: '50%', background: '#ddd' }}
        />
      ) : (
        <ColoredAvatar emp={myEmp} name={name} size={28} />
      )}

      <Chip
        label={`${name} • ${elapsed}`}
        size="small"
        sx={{ fontWeight: 600, bgcolor: '#eef6ff' }}
      />

      <Tooltip title="Checkout">
        <IconButton size="small" onClick={() => onCheckoutClick && onCheckoutClick(myEmp)}>
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Checked-in list">
        <IconButton size="small" onClick={handleOpen}>
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={menuAnchor} open={open} onClose={handleClose} keepMounted>
        {others.length === 0 && <MenuItem disabled>No other employees checked-in</MenuItem>}
        {others.map((c) => {
          // Prioritize fresh employee data from activeEmployees over cached empData
          const empId = c?.empData?._id || c?.empId;
          const freshEmp = activeEmployees.find((e) => e?._id === empId);
          const emp = freshEmp || c?.empData || {};
          const otherName = emp?.staff_name || emp?.name || 'Employee';
          const st = parseDateTime(c?.checkInDate, c?.checkInTime);
          const elapsedTxt = formatElapsed(st);
          const url = resolveEmpImage(emp);
          return (
            <MenuItem key={c?._id || (emp?._id + c?.checkInTime)} sx={{ gap: 1 }}>
              {url ? (
                <CachedImage src={url} alt={otherName} style={{ width: 22, height: 22, borderRadius: '50%' }} fallbackStyle={{ width: 22, height: 22, borderRadius: '50%', background: '#ddd' }} />
              ) : (
                <ColoredAvatar emp={emp} name={otherName} size={22} />
              )}
              <span style={{ fontSize: 13, fontWeight: 600 }}>{otherName}</span>
              <span style={{ fontSize: 12, color: '#555', marginLeft: 'auto', marginRight: 8 }}>{elapsedTxt}</span>
              <Tooltip title="Checkout">
                <IconButton size="small" onClick={() => { handleClose(); if (typeof onCheckoutClick === 'function') onCheckoutClick(emp); }}>
                  <LogoutIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

export default CheckInStatusIndicator;
